import { SessionStatus } from '@threadline/contracts';
import { Check, ChevronsUpDown, Languages, LogOut, Moon, Monitor, Plus, Sun } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/auth/auth-context';
import { useAccess } from '@/hooks/use-access';
import { StatusDot } from '@/components/common/status-dot';
import { useSessions } from '@/hooks/use-sessions';
import { useTheme } from '@/hooks/use-theme';
import { LANGS, useI18n } from '@/i18n';
import { cn, initials, initialsColour } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown';
import { NAV } from './nav-items';

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user, logout } = useAuth();
  const { can } = useAccess();
  const { data: sessions } = useSessions();
  const location = useLocation();
  const { t } = useI18n();

  const connected = sessions?.filter((s) => s.status === SessionStatus.CONNECTED).length ?? 0;
  const attention =
    sessions?.filter(
      (s) => s.status === SessionStatus.ERROR || s.status === SessionStatus.DISCONNECTED,
    ).length ?? 0;

  return (
    <aside className="flex h-full w-sidebar shrink-0 flex-col border-r border-line bg-surface-sunken">
      <div className="flex h-topbar items-center gap-2 px-3">
        <Link to="/" className="flex items-center gap-2" onClick={onNavigate}>
          <Mark />
          <span className="text-base font-semibold tracking-tight">Threadline</span>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-3">
        {NAV.map((group) => {
          // A menu the role cannot open is worse than no menu: it looks like a
          // way in and ends in a refusal.
          const items = group.items.filter((item) => can(item.permission));
          if (!items.length) return null;

          return (
            <div key={group.labelKey} className="mb-4">
              <p className="label-caps px-2 pb-1.5">{t(group.labelKey)}</p>
              <ul className="space-y-px">
                {items.map((item) => {
                  const active = item.prefix
                    ? location.pathname.startsWith(item.to)
                    : location.pathname === item.to;

                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        onClick={onNavigate}
                        className={cn(
                          'group flex h-7 items-center gap-2 rounded px-2 text-sm transition-colors',
                          active
                            ? 'bg-surface font-medium text-ink shadow-inset'
                            : 'text-ink-muted hover:bg-surface hover:text-ink',
                        )}
                      >
                        <item.icon
                          className={cn(
                            'h-3.5 w-3.5 shrink-0',
                            active ? 'text-ink' : 'text-ink-subtle group-hover:text-ink-muted',
                          )}
                          strokeWidth={1.75}
                        />
                        <span className="truncate">{t(item.labelKey)}</span>

                        {item.to === '/sessions' && attention > 0 && (
                          <span
                            className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-sm bg-danger-soft px-1 text-2xs font-medium text-danger tabular-nums"
                            title={t(
                              attention === 1 ? 'nav.attention.one' : 'nav.attention.other',
                              { count: attention },
                            )}
                          >
                            {attention}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-line px-2 py-2">
        <div className="mb-2 flex items-center justify-between px-2 py-1">
          <span className="flex items-center gap-1.5 text-xs text-ink-muted">
            <StatusDot status={connected > 0 ? SessionStatus.CONNECTED : SessionStatus.STOPPED} />
            {t('nav.live', { count: connected })}
          </span>
          {can('sessions.create') && (
            <Link
              to="/sessions?new=1"
              onClick={onNavigate}
              className="flex h-5 w-5 items-center justify-center rounded text-ink-subtle transition-colors hover:bg-surface hover:text-ink"
              aria-label={t('nav.newSession')}
              title={t('nav.newSession')}
            >
              <Plus className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>

        <AccountMenu name={user?.name ?? ''} email={user?.email ?? ''} onLogout={logout} />
      </div>
    </aside>
  );
}

function AccountMenu({
  name,
  email,
  onLogout,
}: {
  name: string;
  email: string;
  onLogout: () => void;
}) {
  const { theme, setTheme } = useTheme();
  const { t, lang, setLang } = useI18n();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left transition-colors hover:bg-surface">
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-2xs font-semibold text-white"
          style={{ backgroundColor: initialsColour(name || email) }}
          aria-hidden
        >
          {initials(name || email)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium leading-tight">{name}</span>
          <span className="block truncate text-xs leading-tight text-ink-subtle">{email}</span>
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-ink-subtle" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" side="top" className="w-[214px]">
        <DropdownMenuLabel>{t('account.appearance')}</DropdownMenuLabel>
        {(
          [
            ['light', 'account.theme.light', Sun],
            ['dark', 'account.theme.dark', Moon],
            ['system', 'account.theme.system', Monitor],
          ] as const
        ).map(([value, labelKey, Icon]) => (
          <DropdownMenuItem key={value} onSelect={() => setTheme(value)}>
            <Icon className="h-3.5 w-3.5" />
            {t(labelKey)}
            {theme === value && <Check className="ml-auto h-3.5 w-3.5 text-ink-subtle" />}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        {/* Sits beside the theme picker rather than in Settings: both are
            per-person display preferences, and Settings is admin-only. */}
        <DropdownMenuLabel>{t('account.language')}</DropdownMenuLabel>
        {LANGS.map(({ value, label }) => (
          <DropdownMenuItem key={value} onSelect={() => setLang(value)}>
            <Languages className="h-3.5 w-3.5" />
            {label}
            {lang === value && <Check className="ml-auto h-3.5 w-3.5 text-ink-subtle" />}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout}>
          <LogOut className="h-3.5 w-3.5" />
          {t('account.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Wordmark: three threads converging. Drawn inline so the shell has no image
 * request on first paint.
 */
function Mark() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" aria-hidden>
      <path
        d="M3 4.5h14M3 10h9M3 15.5h5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="16" cy="15.5" r="2.2" className="fill-ok" />
    </svg>
  );
}
